<template>
  <material-card
  :title="$t('globals.drugs')"
    :color="$vuetify.theme.themes.light.secondary"
    icon="supervised_user_circle"
  >
    <v-card-text class="content">
      <v-stepper
        non-linear
        v-model="step"
        vertical
        class="elevation-0"
        style="width: 100%;"
      >
        <v-stepper-step :complete="step > 1" step="1" :editable="step <= 2">
          {{ $t("components.drugs.drugsimport.download_template") }}
          <small>
            {{ $t("components.drugs.drugsimport.download_and_use") }}
          </small>
        </v-stepper-step>
        <v-stepper-content step="1">
          <v-container>
            <div class="row">
              <div class="col-12 col-md-12">
                <v-btn
                  color="success"
                  class="ma-1"
                  @click="downloadAll"
                  :loading="downloadingAll"
                >
                  {{ $t("components.drugs.drugsimport.all_drugs") }}
                </v-btn>
                <v-btn
                  color="success"
                  class="ma-1"
                  @click="downloadEmpty"
                  :loading="downloadingEmpty"
                >
                  {{ $t("components.membersimportwizardcomponent.empty") }}
                </v-btn>
                <v-btn color="primary" class="ma-1" @click="step++">
                  {{ $t("globals.continue") }}
                </v-btn>
              </div>
            </div>
          </v-container>
        </v-stepper-content>

        <v-stepper-step :complete="step > 2" step="2" editable>
          {{ $t("components.drugs.drugsimport.import_replace") }}
          <small>
            {{ $t("components.drugs.drugsimport.choose_the_file") }}
          </small>
        </v-stepper-step>
        <v-stepper-content step="2">
          <v-form :disabled="loading" ref="form" v-model="valid">
            <v-container>
              <div class="row">
                <div class="col-12 col-md-4">
                  <trans-multi-select2
                    v-model="selectedMemberGroups"
                    :items="memberGroups"
                    :label="$t('globals.member_groups')"
                    field="Name"
                    itemValue="IdentityId"
                  ></trans-multi-select2>
                </div>
                <div class="col-12 col-md-6">
                  <v-file-input v-model="file" class="required"
                    :rules="[$rules.required]" :label="$t('views.plandownloadcenter.file')"></v-file-input>
                </div>
                <div class="col-12 col-md-2">
                  <v-btn
                    color="primary"
                    :disabled="!valid"
                    :loading="loading"
                    @click="upload"
                  >
                    {{ $t("globals.continue") }}
                  </v-btn>
                </div>
              </div>
            </v-container>
          </v-form>
        </v-stepper-content>

        <v-stepper-step :complete="step > 3" step="3">
          {{ $t("components.drugs.drugsimport.assign_drug_list") }}
          <small>
            {{ $t("components.drugs.drugsimport.select_coverages") }}
          </small>
        </v-stepper-step>
        <v-stepper-content step="3">
          <template v-if="errors.length > 0">
            <v-alert
            type="error" 
            dismissible>
            {{ $t(`components.drugs.drugsimport.error`) }}
            </v-alert>
            <v-alert
              v-for="(error, i) in errors"
              dense
              type="error"
              :key="i"
              dismissible
            >
              {{ $t(`components.drugs.drugsimport.error_{0}_{1}_{2}`, [ error.Row, error.Column, error.Message]) }}
            </v-alert>
          </template>
          <drug-coverage-assignments
            @saved="$emit('cancel', false)"
          ></drug-coverage-assignments>
        </v-stepper-content>
      </v-stepper>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn text :loading="loading" @click="$emit('cancel')">
        {{ $t("globals.close") }}
      </v-btn>
    </v-card-actions>
    <error-handler :error="error"></error-handler>
  </material-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DrugCoverageAssignments from "./DrugCoverageAssignments";
import { Downloader, ExcelData } from "pbas-vue-components";
import DrugHeaders from "../../headers/Drugs";
import ErrorHandler from "../../components/ErrorHandler";
import TransMultiSelect2 from "../TransMultiSelect2.vue";

export default {
  components: { DrugCoverageAssignments, ErrorHandler, TransMultiSelect2 },
  props: {
    provider: Object,
  },
  data: function() {
    return {
      file: null,
      loading: false,
      error: null,
      step: 1,
      show: false,
      downloadingEmpty: false,
      downloadingAll: false,
      memberGroups: [],
      valid: false,
      selectedMemberGroups:[],
      errors: []
    };
  },
  async mounted() {
    this.step = 1;
    this.file = null;
    this.loading = true;
    try {
      this.memberGroups = (await this.getMemberGroups({
        plan: this.selectedPlan.Id,
        url: "?$expand=Translations",
      })).data.value;
    } catch (error) {
      this.error = error;
    } finally {
      this.loading = false;
    }
  },
  computed: {
    ...mapGetters("common", { selectedPlan: "plan", settings: "settings" }),
  },
  methods: {
    ...mapActions("downloadCenter", ["importDrugs"]),
    ...mapActions("plansMembers", ["getMemberGroups"]),
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    async downloadEmpty() {
      this.downloadingEmpty = true;
      try {
        const blob = await this.$exporters[1].exportData(
          this.provider,
          { items: [] },
          DrugHeaders.slice(1, 4)
        );
        new Downloader().download("drugs-template.xlsx", blob);
      } catch (e) {
        this.error = e;
      } finally {
        this.downloadingEmpty = false;
      }
    },
    async downloadAll() {
      this.downloadingAll = true;
      try {
        const items = await this.provider.dataProvider.fetchAsync();
        const blob = await this.$exporters[1].exportData(
          this.provider,
          { items: items.items },
          DrugHeaders.slice(1, 4)
        );
        new Downloader().download("drugs.xlsx", blob);
      } catch (e) {
        this.error = e;
      } finally {
        this.downloadingAll = false;
      }
    },
    async upload() {
      try {
        console.log(this.selectedMemberGroups);
        this.error = null;
      this.loading = true;
        const data = await this.toBase64(this.file);
        var e = await this.importDrugs({
            base64: data,
            name: this.file.name,
            planId: this.selectedPlan.Id,
            memberGroupIdentityIds: this.selectedMemberGroups.map(o=>o.IdentityId)
          })
          console.log(e, e.data);
          this.errors = e;
        this.$emit("upload");
        this.step++;
        //this.$emit("update:show", false);
      } catch (e) {
        this.error = e;
      }
      finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.content /deep/ .v-card__title {
  display: none;
}
</style>
